export type PerformanceDatum = {
  task: string
  baseline: number
  baselineSd: number
  dats: number
  datsSd: number
  baselineSuccess: string
  datsSuccess: string
}

export type TaskMarker = 'circle' | 'square' | 'triangle-up' | 'diamond' | 'plus' | 'triangle-down'

export type AuditDatum = {
  task: string
  durationGain: number
  durationSd: number
  coverageGain: number
  coverageSd: number
  colour: string
  marker: TaskMarker
}

export type VideoVariant = {
  label: string
  video: string
  poster: string
}

export type VideoTask = {
  slug: string
  title: string
  description: string
  role: string
  colour: string
  marker: TaskMarker
  rawVariants: VideoVariant[]
  datsVariants: VideoVariant[]
}

export const performanceData: PerformanceDatum[] = [
  { task: 'TowelHang', baseline: 150.4, baselineSd: 4.7, dats: 83.6, datsSd: 0.8, baselineSuccess: '17/25', datsSuccess: '19/25' },
  { task: 'BinTowel', baseline: 83.6, baselineSd: 5.5, dats: 57.5, datsSd: 5.8, baselineSuccess: '20/25', datsSuccess: '21/25' },
  { task: 'ToteCards', baseline: 94.1, baselineSd: 11.7, dats: 50.2, datsSd: 5.0, baselineSuccess: '20/25', datsSuccess: '20/25' },
  { task: 'BagTape', baseline: 55.5, baselineSd: 0.6, dats: 31.4, datsSd: 1.1, baselineSuccess: '25/25', datsSuccess: '25/25' },
  { task: 'LidEraser', baseline: 63.1, baselineSd: 0.6, dats: 31.6, datsSd: 0.8, baselineSuccess: '25/25', datsSuccess: '25/25' },
  { task: 'TrayWipe', baseline: 52.0, baselineSd: 1.0, dats: 32.4, datsSd: 1.5, baselineSuccess: '19/25', datsSuccess: '19/25' },
]

export const auditData: AuditDatum[] = [
  { task: 'TowelHang', durationGain: 19.8, durationSd: 7.0, coverageGain: 66.5, coverageSd: 16.0, colour: '#0072B2', marker: 'circle' },
  { task: 'BinTowel', durationGain: 23.9, durationSd: 5.8, coverageGain: 41.4, coverageSd: 8.5, colour: '#E69F00', marker: 'square' },
  { task: 'ToteCards', durationGain: -0.8, durationSd: 0.7, coverageGain: 94.0, coverageSd: 16.4, colour: '#009E73', marker: 'triangle-up' },
  { task: 'BagTape', durationGain: 6.0, durationSd: 1.7, coverageGain: 16.3, coverageSd: 4.2, colour: '#D55E00', marker: 'diamond' },
  { task: 'LidEraser', durationGain: 12.7, durationSd: 2.5, coverageGain: 54.0, coverageSd: 10.3, colour: '#CC79A7', marker: 'plus' },
  { task: 'TrayWipe', durationGain: 0.1, durationSd: 0.2, coverageGain: 61.7, coverageSd: 10.6, colour: '#56B4E9', marker: 'triangle-down' },
]

const variant = (slug: string, file: string, label: string): VideoVariant => ({
  label,
  video: `videos/${slug}/${file}.mp4`,
  poster: `videos/${slug}/${file}.webp`,
})

export const videoTasks: VideoTask[] = [
  {
    slug: 'towelhang',
    title: 'TowelHang',
    role: 'Maintain hanger geometry · fold · lift',
    description: 'One arm maintains the hanger while the remaining arms prepare, lift and place the towel.',
    colour: '#0072B2',
    marker: 'circle',
    rawVariants: [variant('towelhang', 'view-01', 'Raw 01')],
    datsVariants: [variant('towelhang', 'primary', 'DATS 01'), variant('towelhang', 'view-02', 'DATS 02')],
  },
  {
    slug: 'bintowel',
    title: 'BinTowel',
    role: 'Transfer towel · stage container',
    description: 'Container staging progresses while the towel transfer continues across the workspace.',
    colour: '#E69F00',
    marker: 'square',
    rawVariants: [
      variant('bintowel', 'primary', 'Raw 01'),
      variant('bintowel', 'view-02', 'Raw 02'),
      variant('bintowel', 'view-04', 'Raw 03'),
    ],
    datsVariants: [variant('bintowel', 'view-01', 'DATS 01'), variant('bintowel', 'view-03', 'DATS 02')],
  },
  {
    slug: 'totecards',
    title: 'ToteCards',
    role: 'Maintain aperture · transfer cards',
    description: 'Two arms preserve the tote opening while the third retrieves and transfers the card box.',
    colour: '#009E73',
    marker: 'triangle-up',
    rawVariants: [variant('totecards', 'view-01', 'Raw 01')],
    datsVariants: [variant('totecards', 'primary', 'DATS 01')],
  },
  {
    slug: 'bagtape',
    title: 'BagTape',
    role: 'Form aperture · prepare tape · insert',
    description: 'Aperture maintenance continues while the independent tape-preparation stream advances.',
    colour: '#D55E00',
    marker: 'diamond',
    rawVariants: [
      variant('bagtape', 'view-03', 'Raw 01'),
      variant('bagtape', 'view-07', 'Raw 02'),
      variant('bagtape', 'view-08', 'Raw 03'),
    ],
    datsVariants: [
      variant('bagtape', 'primary', 'DATS 01'),
      variant('bagtape', 'view-01', 'DATS 02'),
      variant('bagtape', 'view-02', 'DATS 03'),
      variant('bagtape', 'view-04', 'DATS 04'),
      variant('bagtape', 'view-05', 'DATS 05'),
      variant('bagtape', 'view-06', 'DATS 06'),
    ],
  },
  {
    slug: 'lideraser',
    title: 'LidEraser',
    role: 'Position fixture · acquire two erasers',
    description: 'Fixture positioning and dual-object acquisition are coordinated within the same task phase.',
    colour: '#CC79A7',
    marker: 'plus',
    rawVariants: [
      variant('lideraser', 'view-03', 'Raw 01'),
      variant('lideraser', 'view-04', 'Raw 02'),
      variant('lideraser', 'view-05', 'Raw 03'),
    ],
    datsVariants: [
      variant('lideraser', 'primary', 'DATS 01'),
      variant('lideraser', 'view-01', 'DATS 02'),
      variant('lideraser', 'view-02', 'DATS 03'),
    ],
  },
  {
    slug: 'traywipe',
    title: 'TrayWipe',
    role: 'Stabilise tray · prepare towel · wipe',
    description: 'Tray support remains active while towel preparation and wiping begin earlier.',
    colour: '#56B4E9',
    marker: 'triangle-down',
    rawVariants: [variant('traywipe', 'view-01', 'Raw 01'), variant('traywipe', 'view-02', 'Raw 02')],
    datsVariants: [variant('traywipe', 'primary', 'DATS 01')],
  },
]
